import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { apiAsync } from "utils/api"
import { SavedBadge } from "components/shared/saved_badge"
import { Checkbox, Pane, RadioButton } from "components/ui"
import { Box, Button } from "@planningcenter/tapestry-react"
import RegistrantCollectionOptions from "./RegistrantCollectionOptions"
import useFlipperFlag from "hooks/use_flipper_flag"

let saveTimeout = null

const GuestModeOption = ({ checked, disabled, handleGuestModeChange, helpText }) => (
  <>
    <Checkbox
      text="Require login"
      htmlFor="guest_mode"
      value="guest_mode"
      checked={checked}
      disabled={disabled}
      onSelect={handleGuestModeChange}
    />
    <div className="font-size-4 mt-1 pl-3 c-neutral-600">{helpText}</div>
  </>
)

GuestModeOption.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  helpText: PropTypes.node,
  handleGuestModeChange: PropTypes.func
}

const RegistrationSettingsForm = ({
  event: eventFromProps,
  isUnifiedDetailedSignupsCCWEnabled,
  isActingStaff
}) => {
  const simplePaymentsEnabled = useFlipperFlag("ROLLOUT_simple_payments")

  const [event, setEvent] = useState(eventFromProps)
  const [allowedRegistrations, setAllowedRegistrations] = useState(event.allowedRegistrations)
  const [allowHouseholdRegistrations, setAllowHouseholdRegistrations] = useState(
    event.allowHouseholdRegistrations
  )
  const [allowOutsideRegistrations, setOutsideRegistrations] = useState(
    event.allowOutsideRegistrations
  )
  const [registrantCollectionOptions, setRegistrantCollectionOptions] = useState(
    event.registrantCollectionOptions
  )
  const [guestMode, setGuestMode] = useState(event.guestMode)
  const [isEventSaved, setIsEventSaved] = useState(false)
  const [registrationType, setRegistrationType] = useState(event.isSimple ? "simple" : "detailed")
  const [_errors, setErrors] = useState({})

  useEffect(() => {
    return () => {
      clearTimeout(saveTimeout)
    }
  }, [])

  const isDetailedEvent = !event.isSimple

  const updateEvent = async params => {
    setErrors([])
    setIsEventSaved(false)

    if (saveTimeout) {
      clearTimeout(saveTimeout)
    }

    const response = await apiAsync.put(event.paths.show, { event: params })

    if (response.errors) {
      setErrors(response.errors)
    } else {
      setEvent(response.event)
      setIsEventSaved(true)
      saveTimeout = setTimeout(() => setIsEventSaved(false), 5000)
    }
  }

  const isCollectingAdditionalRegistrantInformation = ({ address, phone_number }) =>
    address !== "hidden" || phone_number !== "hidden"

  const guestModeHelpText = () => {
    if (
      isDetailedEvent ||
      (!isDetailedEvent && isCollectingAdditionalRegistrantInformation(registrantCollectionOptions))
    ) {
      return "When additional information or online payments are collected, people are required to log in or create an account to register"
    } else if (!isDetailedEvent) {
      return (
        <>
          Requiring login reduces duplicate profiles and enhances security.{" "}
          <a
            href="https://pcoregistrations.zendesk.com/hc/en-us/articles/204462680-Manage-Signup-Settings"
            target="_blank"
            rel="noreferrer"
          >
            Learn more
          </a>
        </>
      )
    } else {
      return "This will require people to login before registering which can help avoid duplicate profiles"
    }
  }

  // TODO: Remove legacy guest mode code when all detailed signups are using the new
  // detailed workflow since that should no longer allow guests.
  const handleLegacyGuestModeChange = ({ target: { checked } }) => {
    const mode = checked ? "enabled" : "disabled"
    setGuestMode(mode)
    updateEvent({ guest_mode: mode })
  }

  const handleGuestModeChange = async ({ target: { checked } }) => {
    const mode = checked ? "disabled" : "default"
    setGuestMode(mode)
    await updateEvent({ guest_mode: mode })
  }

  const handleAllowedRegistrationChange = ({ target: { value } }) => {
    const newGuestMode = value === "household" ? "disabled" : guestMode
    if (value === "household") {
      setGuestMode(newGuestMode)
    }
    setAllowedRegistrations(value)
    updateEvent({ guest_mode: newGuestMode, allowed_registrations: value })
  }

  const handleAllowHouseholdRegistrationChange = ({ target: { value } }) => {
    setAllowHouseholdRegistrations(value)
    updateEvent({ allow_household_registrations: value })
  }

  const handleAllowOutsideRegistrationChange = ({ target: { checked } }) => {
    const value = checked ? "always" : "never"

    setOutsideRegistrations(value)
    updateEvent({ allow_outside_registrations: value })
  }

  const handleRegistrantCollectionOptionsChange = options => {
    const newGuestMode =
      isDetailedEvent || isCollectingAdditionalRegistrantInformation(options)
        ? "disabled"
        : guestMode
    setGuestMode(newGuestMode)
    setRegistrantCollectionOptions(options)
    updateEvent({ guest_mode: newGuestMode, registrant_collection_options: options })
  }

  const handleConvertSignup = () => {
    const nextType = registrationType == "simple" ? "detailed" : "simple"
    const swalText =
      "Converting a signup can break things. <b>It is safest on a new signup \
     that was just created.</b> If any settings have been changed on this signup since it \
     was created, follow \
     <a href='https://app.getguru.com/card/TLM9qMMc/Converting-Signup-Types-Detailed-and-Simple>'>\
      this checklist</a> to ensure that it can be converted successfully."
    const textDiv = document.createElement("div")
    textDiv.innerHTML = swalText
    alertModal(
      {
        title: "Caution!",
        content: textDiv,
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "Convert Signup",
        customClass: "success",
        closeOnConfirm: true
      },
      () => {
        updateEvent({ registration_type: nextType })
        setRegistrationType(nextType)
      }
    )
  }

  const LegacyGuestModeOption = () => (
    <Box paddingHorizontal={2} paddingBottom={2}>
      <Checkbox
        disabled={isDetailedEvent && allowedRegistrations !== "any"}
        text="Allow guest registrations"
        htmlFor="guest_mode"
        value="guest_mode"
        checked={guestMode == "enabled"}
        onSelect={handleLegacyGuestModeChange}
      />
      <div className="font-size-4 mt-1 pl-3 c-neutral-600">
        {allowedRegistrations !== "any"
          ? "When profile or household information is collected, people are required to log in or create an account to register."
          : "This will enable people to register without logging in but may result in duplicate profiles"}
      </div>
    </Box>
  )

  const renderLegacyDetailedSignupsCCWOptions = () => {
    return (
      <>
        <h4 className="label">People can register</h4>
        <RadioButton
          name="allowed_registrations"
          text="Self only"
          htmlFor="self"
          value="self"
          checked={allowedRegistrations === "self"}
          onSelect={handleAllowedRegistrationChange}
        />
        <RadioButton
          name="allowed_registrations"
          text="Self and household members"
          htmlFor="household"
          value="household"
          checked={allowedRegistrations === "household"}
          onSelect={handleAllowedRegistrationChange}
        />
        <RadioButton
          name="allowed_registrations"
          text="Self, household members, and others outside their household"
          htmlFor="any"
          value="any"
          checked={allowedRegistrations === "any"}
          onSelect={handleAllowedRegistrationChange}
        />
      </>
    )
  }

  const renderUnifiedDetailedSignupsCCWOptions = () => {
    return (
      <>
        <h4 className="label">Allow people to add household members during registration</h4>
        <RadioButton
          name="allow_household_registrations"
          text="Always"
          htmlFor="always"
          value="always"
          checked={allowHouseholdRegistrations === "always"}
          onSelect={handleAllowHouseholdRegistrationChange}
        />

        <RadioButton
          name="allow_household_registrations"
          text="Only if they don't have a household yet"
          htmlFor="new"
          value="new"
          checked={allowHouseholdRegistrations === "new"}
          onSelect={handleAllowHouseholdRegistrationChange}
        />

        <RadioButton
          name="allow_household_registrations"
          text="Never"
          htmlFor="never"
          value="never"
          checked={allowHouseholdRegistrations === "never"}
          onSelect={handleAllowHouseholdRegistrationChange}
        />

        <Box paddingTop={2}>
          <Checkbox
            text="Allow people to register others outside their household"
            htmlFor="allow_outside_registrations"
            value="allow_outside_registrations"
            checked={allowOutsideRegistrations === "always"}
            onSelect={handleAllowOutsideRegistrationChange}
          />
        </Box>
      </>
    )
  }

  return (
    <Pane>
      <Pane.Header>
        <Pane.Title>Registrant options</Pane.Title>
        {isEventSaved ? <SavedBadge /> : null}
        {isActingStaff && (
          <Button
            className="staff-only"
            size="md"
            variant="outline"
            onClick={handleConvertSignup}
            title={`Convert signup (${registrationType})`}
          />
        )}
      </Pane.Header>
      <Pane.Body padding={0}>
        {isDetailedEvent ? (
          <>
            {isUnifiedDetailedSignupsCCWEnabled ? (
              <>
                <Box padding={2}>
                  <Box paddingBottom={2}>
                    <RegistrantCollectionOptions
                      onSelect={handleRegistrantCollectionOptionsChange}
                      registrantCollectionOptions={registrantCollectionOptions}
                    />
                  </Box>
                  {renderUnifiedDetailedSignupsCCWOptions()}
                </Box>
                <Box padding={2} className="line">
                  <GuestModeOption
                    checked={guestMode === "disabled"}
                    disabled={guestMode === "disabled"}
                    handleGuestModeChange={handleGuestModeChange}
                    helpText={guestModeHelpText()}
                  />
                </Box>
              </>
            ) : (
              <Box padding={2}>{renderLegacyDetailedSignupsCCWOptions()}</Box>
            )}
            {!isUnifiedDetailedSignupsCCWEnabled && <LegacyGuestModeOption />}
          </>
        ) : (
          <>
            <Box padding={2}>
              <RegistrantCollectionOptions
                onSelect={handleRegistrantCollectionOptionsChange}
                registrantCollectionOptions={registrantCollectionOptions}
              />
            </Box>
            <Box padding={2} className="line">
              <GuestModeOption
                checked={guestMode === "disabled"}
                disabled={
                  event.hasCost ||
                  isCollectingAdditionalRegistrantInformation(registrantCollectionOptions)
                }
                handleGuestModeChange={handleGuestModeChange}
                helpText={guestModeHelpText()}
              />
            </Box>
          </>
        )}
      </Pane.Body>
    </Pane>
  )
}

RegistrationSettingsForm.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number,
    allowHouseholdRegistrations: PropTypes.string,
    allowOutsideRegistrations: PropTypes.string,
    allowedRegistrations: PropTypes.string,
    guestMode: PropTypes.string,
    isSimple: PropTypes.bool,
    paths: PropTypes.shape({
      show: PropTypes.string
    })
  }),
  isUnifiedDetailedSignupsCCWEnabled: PropTypes.bool,
  isActingStaff: PropTypes.bool
}

export default RegistrationSettingsForm
